import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Search } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { cn } from '@/lib/utils';

interface TimezoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentTimezone: string;
  onSave: (timezone: string) => Promise<void>;
}

/**
 * Common IANA timezones grouped by region with readable labels.
 * Covers Europe (primary market), plus major world zones for travellers.
 */
const TIMEZONE_OPTIONS: { value: string; labelDe: string; labelEn: string }[] = [
  // Europe
  { value: 'Europe/Berlin', labelDe: 'Berlin (MEZ)', labelEn: 'Berlin (CET)' },
  { value: 'Europe/Vienna', labelDe: 'Wien (MEZ)', labelEn: 'Vienna (CET)' },
  { value: 'Europe/Zurich', labelDe: 'Z\u00fcrich (MEZ)', labelEn: 'Zurich (CET)' },
  { value: 'Europe/Amsterdam', labelDe: 'Amsterdam (MEZ)', labelEn: 'Amsterdam (CET)' },
  { value: 'Europe/Brussels', labelDe: 'Br\u00fcssel (MEZ)', labelEn: 'Brussels (CET)' },
  { value: 'Europe/Paris', labelDe: 'Paris (MEZ)', labelEn: 'Paris (CET)' },
  { value: 'Europe/Rome', labelDe: 'Rom (MEZ)', labelEn: 'Rome (CET)' },
  { value: 'Europe/Madrid', labelDe: 'Madrid (MEZ)', labelEn: 'Madrid (CET)' },
  { value: 'Europe/Warsaw', labelDe: 'Warschau (MEZ)', labelEn: 'Warsaw (CET)' },
  { value: 'Europe/Prague', labelDe: 'Prag (MEZ)', labelEn: 'Prague (CET)' },
  { value: 'Europe/Stockholm', labelDe: 'Stockholm (MEZ)', labelEn: 'Stockholm (CET)' },
  { value: 'Europe/Copenhagen', labelDe: 'Kopenhagen (MEZ)', labelEn: 'Copenhagen (CET)' },
  { value: 'Europe/London', labelDe: 'London (WEZ)', labelEn: 'London (GMT)' },
  { value: 'Europe/Dublin', labelDe: 'Dublin (WEZ)', labelEn: 'Dublin (GMT)' },
  { value: 'Europe/Lisbon', labelDe: 'Lissabon (WEZ)', labelEn: 'Lisbon (WET)' },
  { value: 'Europe/Athens', labelDe: 'Athen (OEZ)', labelEn: 'Athens (EET)' },
  { value: 'Europe/Helsinki', labelDe: 'Helsinki (OEZ)', labelEn: 'Helsinki (EET)' },
  { value: 'Europe/Bucharest', labelDe: 'Bukarest (OEZ)', labelEn: 'Bucharest (EET)' },
  { value: 'Europe/Istanbul', labelDe: 'Istanbul (T\u00fcrkei)', labelEn: 'Istanbul (Turkey)' },
  { value: 'Europe/Moscow', labelDe: 'Moskau (MSK)', labelEn: 'Moscow (MSK)' },
  // Americas
  { value: 'America/New_York', labelDe: 'New York (EST)', labelEn: 'New York (EST)' },
  { value: 'America/Chicago', labelDe: 'Chicago (CST)', labelEn: 'Chicago (CST)' },
  { value: 'America/Denver', labelDe: 'Denver (MST)', labelEn: 'Denver (MST)' },
  { value: 'America/Los_Angeles', labelDe: 'Los Angeles (PST)', labelEn: 'Los Angeles (PST)' },
  { value: 'America/Sao_Paulo', labelDe: 'S\u00e3o Paulo (BRT)', labelEn: 'S\u00e3o Paulo (BRT)' },
  // Asia / Oceania
  { value: 'Asia/Dubai', labelDe: 'Dubai (GST)', labelEn: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', labelDe: 'Indien (IST)', labelEn: 'India (IST)' },
  { value: 'Asia/Bangkok', labelDe: 'Bangkok (ICT)', labelEn: 'Bangkok (ICT)' },
  { value: 'Asia/Shanghai', labelDe: 'Shanghai (CST)', labelEn: 'Shanghai (CST)' },
  { value: 'Asia/Tokyo', labelDe: 'Tokio (JST)', labelEn: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', labelDe: 'Sydney (AEST)', labelEn: 'Sydney (AEST)' },
  { value: 'Pacific/Auckland', labelDe: 'Auckland (NZST)', labelEn: 'Auckland (NZST)' },
];

function getTimezoneLabel(tz: string, lang: string): string {
  const option = TIMEZONE_OPTIONS.find((o) => o.value === tz);
  if (option) return lang === 'en' ? option.labelEn : option.labelDe;
  // Fallback: format IANA identifier as readable label
  return tz.replace(/_/g, ' ').replace(/\//g, ' / ');
}

/** Exported helper for use in ProfilePage to display the current timezone label. */
export function getTimezoneDisplayLabel(tz: string, lang: string): string {
  return getTimezoneLabel(tz, lang);
}

export function TimezoneModal({ isOpen, onClose, currentTimezone, onSave }: TimezoneModalProps) {
  const { t, i18n } = useTranslation(['profile', 'common']);
  const [selected, setSelected] = useState(currentTimezone);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelected(currentTimezone);
      setSearchQuery('');
    }
  }, [isOpen, currentTimezone]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return TIMEZONE_OPTIONS;
    const q = searchQuery.toLowerCase();
    return TIMEZONE_OPTIONS.filter(
      (o) =>
        o.value.toLowerCase().includes(q) ||
        o.labelDe.toLowerCase().includes(q) ||
        o.labelEn.toLowerCase().includes(q),
    );
  }, [searchQuery]);

  // Ensure the user's current timezone is always in the list even if not in TIMEZONE_OPTIONS
  const isCurrentInList = TIMEZONE_OPTIONS.some((o) => o.value === currentTimezone);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (selected === currentTimezone) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await onSave(selected);
      onClose();
    } catch (err) {
      console.error('Error saving timezone:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <h2>{t('profile:timezone.title')}</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content space-y-4">
            <p className="text-sm text-white/70">
              {t('profile:timezone.description')}
            </p>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('profile:timezone.searchPlaceholder')}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:border-pp-orange/50"
              />
            </div>

            <div className="space-y-2 max-h-[45vh] overflow-y-auto">
              {/* Show current timezone at top if it's not in the standard list */}
              {!isCurrentInList && (
                <button
                  onClick={() => setSelected(currentTimezone)}
                  className={cn(
                    'w-full p-3 rounded-[15px] border text-left transition-all',
                    selected === currentTimezone
                      ? 'bg-pp-orange/20 border-pp-orange'
                      : 'bg-white/10 border-white/20 hover:bg-white/15',
                  )}
                >
                  <p className="font-medium text-white text-sm">
                    {getTimezoneLabel(currentTimezone, i18n.language)}
                  </p>
                </button>
              )}
              {filteredOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelected(option.value)}
                  className={cn(
                    'w-full p-3 rounded-[15px] border text-left transition-all',
                    selected === option.value
                      ? 'bg-pp-orange/20 border-pp-orange'
                      : 'bg-white/10 border-white/20 hover:bg-white/15',
                  )}
                >
                  <p className="font-medium text-white text-sm">
                    {i18n.language === 'en' ? option.labelEn : option.labelDe}
                  </p>
                </button>
              ))}
              {filteredOptions.length === 0 && (
                <p className="text-center text-white/50 text-sm py-4">
                  {t('profile:timezone.noResults')}
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 disabled:opacity-50"
            >
              {t('common:actions.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
            >
              {isSaving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                t('common:actions.save')
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
